// Resolves core runtime script metadata and `data-luis-run` tokens.
// Shared by runtime asset lookup and runtime script security policy.

export type RuntimeCoreScriptInfo = {
  src: string
  baseHref: string
  origin: string
  minified: boolean
  runTokens: string[]
}

let capturedCoreScriptSrc = ''
let capturedRunAttribute = ''

// Exposes one capture helper for the current runtime core script element.
export function captureRuntimeCoreScriptElement(): void {
  // Stop capture outside browser environments.
  if (!isBrowser()) return

  const current = document.currentScript
  // Skip capture when the current script is not one external core runtime script.
  if (!(current instanceof HTMLScriptElement) || !current.src) return

  const parsed = parseCoreScriptInfo(current.src, current.getAttribute('data-luis-run'))
  // Skip capture when the current script is not one recognized runtime core bundle.
  if (!parsed) return

  capturedCoreScriptSrc = parsed.src
  capturedRunAttribute = current.getAttribute('data-luis-run') ?? ''
}

// Exposes metadata for the active runtime core script when one is detectable.
export function resolveRuntimeCoreScriptInfo(): RuntimeCoreScriptInfo | null {
  // Stop resolution outside browser environments.
  if (!isBrowser()) return null

  // Reuse the captured core-script source when available.
  if (capturedCoreScriptSrc) {
    const parsed = parseCoreScriptInfo(capturedCoreScriptSrc, capturedRunAttribute)
    // Return parsed metadata when the captured source is still valid.
    if (parsed) return parsed
  }

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
  // Scan scripts from end to start for the nearest runtime core bundle.
  for (let index = scripts.length - 1; index >= 0; index -= 1) {
    const parsed = parseCoreScriptInfo(scripts[index].src, scripts[index].getAttribute('data-luis-run'))
    // Return the first valid runtime core-script match.
    if (parsed) return parsed
  }

  return null
}

// Parse runtime core-script metadata from one external script tag payload.
function parseCoreScriptInfo(scriptSrc: string, runAttribute: string | null): RuntimeCoreScriptInfo | null {
  // Reject empty script-source values.
  if (!scriptSrc) return null

  try {
    const parsedUrl = new URL(scriptSrc, document.baseURI)
    const pathname = parsedUrl.pathname
    // RegExp: matches core runtime bundle names. Example match: "/runtime/lentystyle.min.js"
    if (!/\/lentystyle(?:\.min)?\.js$/i.test(pathname)) return null

    return {
      src: parsedUrl.href,
      baseHref: new URL('.', parsedUrl).toString(),
      origin: parsedUrl.origin,
      minified: /\.min\.js$/i.test(pathname),
      runTokens: tokenizeRunAttribute(runAttribute),
    }
  } catch {
    return null
  }
}

// Parse `data-luis-run` tokens into one normalized deduplicated token list.
function tokenizeRunAttribute(rawValue: string | null): string[] {
  // Return an empty token list when no raw attribute value exists.
  if (!rawValue) return []

  const tokens = new Set<string>()
  // Split the current attribute on commas to preserve the shared token grammar.
  for (const item of rawValue.split(',')) {
    const value = item.trim().toLowerCase()
    // Keep only non-empty normalized tokens.
    if (value) tokens.add(value)
  }

  return Array.from(tokens)
}

// Detect browser runtime before touching DOM-specific globals.
function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && typeof URL === 'function'
}
